export const DOCUMENT_AI_EVENTS = {
    CANCEL: 'cancel',
    SAVE: 'save',
    MINIMIZE: 'minimize',
    SAVE_BULK_PDF: 'save-bulk-pdf',
    COMPLETE_LOADING: 'COMPLETE_LOADING'
};

export const DOCUMENT_AI_TYPES = {
    EPC: 'epc',
    ELECTRICAL: 'electrical',
    CADASTRAL: 'cadastral',
    ASBESTOS: 'asbestos',
    STEDENBOUWKUNDIG: 'stedenbouwkundig',
    FLOOD: 'flood',
    SOIL: 'soil'
} as const;

export type DocumentAIType = typeof DOCUMENT_AI_TYPES[keyof typeof DOCUMENT_AI_TYPES];

export interface DocumentAIFields {
    [key: string]: any;
}

export interface DocumentAIFieldConfidences {
    [key: string]: number;
}

export interface DocumentAIDocumentInfo {
    Id?: number;
    PropertyId?: number;
    Filename?: string;
    DocumentTypeId?: number;
    URL?: string;
    FilePath?: string;
    Comments?: string;
    GroupId?: number;
    PublishOnInternetId?: number;
    IsIncludeInMail?: boolean;
    IsConvertToPDF?: boolean;
    LanguageId?: number;
    IconURL?: string;
}

export interface DocumentAISavePayload {
    type: 'save';
    data: {
        documentType: DocumentAIType;
        fields: DocumentAIFields;
        fieldConfidences: DocumentAIFieldConfidences;
        overallConfidence: number;
        document?: DocumentAIDocumentInfo;
    };
}

export interface DocumentAICancelPayload {
    type: 'cancel';
}

export type DocumentAIPayload = DocumentAISavePayload | DocumentAICancelPayload;

// Field mappings from PDF extraction to property model
// Keys must match the field names sent by the AI extraction service (see AI app types.ts)
export const DOCUMENT_FIELD_MAPPINGS = {
    [DOCUMENT_AI_TYPES.EPC]: {
        // EPC fields from AI app EPCFieldsSchema
        epcType: 'EPCType',
        energyPerformance: 'EnergyPerformance',
        totalConsumption: 'EPCTotal',
        co2Emission: 'CO2Emission',
        date: 'EPCDate',
        expiryDate: 'EPCExpiryDate',
        newConstructionELevel: 'EPCELevel',
        sLevel: 'SLevel',
        uniqueCode: 'UniqueCode',
        energyLabel: 'EPCLabelId',
        expert: 'EPCExpert'
    },
    [DOCUMENT_AI_TYPES.ELECTRICAL]: {
        // Electrical fields from AI app ElectricalFieldsSchema
        compliant: 'ElectricityStateId',
        expert: 'ElectricityExpert',
        date: 'ElectricityDate',
        expiryDate: 'ElectricityExpiredDate',
        uniqueCode: 'ElectricityUniqueCode'
    },
    [DOCUMENT_AI_TYPES.CADASTRAL]: {
        // Cadastral fields from AI app CadastralFieldsSchema
        parcelNumber: 'CadastralNumber',
        cadastralIncome: 'CadastralIncome',
        section: 'CadastralSection',
        municipality: 'CadastralDivision',
        landArea: 'CadastralRegisteredArea'
    },
    [DOCUMENT_AI_TYPES.ASBESTOS]: {
        // Asbestos fields from AI app AsbestosFieldsSchema
        asbestosType: 'AsbestosType',
        date: 'AsbestosDate'
    },
    [DOCUMENT_AI_TYPES.STEDENBOUWKUNDIG]: {
        // Stedenbouwkundig fields from AI app StedenbouwkundigFieldsSchema
        date: 'UrbanInfoDate',
        municipality: 'UrbanInfoCommune',
        reference: 'UrbanInfoNumber',
        buildingPermit: 'HasPermitId',
        buildingPermitRenewable: 'IsPermitRenewable',
        buildingPermitFrom: 'HasPermitStartDate',
        buildingPermitTo: 'HasPermitEndDate',
        subdivisionPermit: 'HasAllotmentPermitId',
        subdivisionPermitRenewable: 'IsAllotmentPermetRenewable',
        subdivisionPermitFrom: 'HasAllotmentPermitStartDate',
        subdivisionPermitTo: 'HasAllotmentPermitEndDate',
        preEmptionRight: 'HasPreemptiveRightsId',
        asBuiltCertificate: 'AsBuiltId',
        summonsAndRecovery: 'HasSummonsId',
        urbanDestination: 'AreaApplicationId',
        heritage: 'HeritageIds'
    },
    [DOCUMENT_AI_TYPES.FLOOD]: {
        // Flood risk fields from AI app FloodFieldsSchema
        // Values arrive as { name, id } objects — id is extracted in mapDocumentAIFields
        floodRisk: 'FloodRiskId',
        plannedRiverbankzone: 'RiverBankZoneId',
        plannedFloodzone: 'FloodZoneId',
        riskAreaForFloodings: 'FloodRiscAreaId',
        gScore: 'FloodZoneGScore',
        pScore: 'FloodZonePScore'
    },
    [DOCUMENT_AI_TYPES.SOIL]: {
        // Soil certificate fields from AI app SoilFieldsSchema.
        // groundCertificate arrives as plain text (Aanwezig/Present/Présent) or { name, id }.
        groundCertificate: 'GroundCertificateId',
        date: 'GroundCertificateDate',
        ourReference: 'GroundCertificateOurReference',
        yourReference: 'GroundCertificateYourReference',
        decision: 'GroundCertificateDecisionNL'
    }
} as const;

// Fields that need to be stored in property.Management
export const MANAGEMENT_FIELDS = [
    'EPCDate',
    'EPCExpert',
    'ElectricityExpert',
    'ElectricityDate',
    'ElectricityExpiredDate',
    'ElectricityUniqueCode',
    'UrbanInfoDate',
    'UrbanInfoCommune',
    'UrbanInfoNumber'
];

// Fields that are date fields and need formatting
export const DATE_FIELDS = [
    'EPCDate',
    'EPCExpiryDate',
    'ElectricityDate',
    'ElectricityExpiredDate',
    'AsbestosDate',
    'UrbanInfoDate',
    'HasPermitStartDate',
    'HasPermitEndDate',
    'HasAllotmentPermitStartDate',
    'HasAllotmentPermitEndDate',
    'GroundCertificateDate'
];

// Fields that need enum lookup conversion (string value -> numeric ID)
// Maps property field name to lookup type name
export const ENUM_FIELDS: Record<string, string> = {
    'EPCLabelId': 'EPCLabel',           // Requires region-aware lookup
    'EPCType': 'EPCType',
    'AsbestosType': 'AsbestosType',
    'ElectricityStateId': 'ElectricityImmoWeb',
    'HasPermitId': 'PermitOptions',
    'HasAllotmentPermitId': 'PermitOptions',
    'HasPreemptiveRightsId': 'PermitOptions',
    'AsBuiltId': 'PermitOptions',
    'HasSummonsId': 'HasSummon',
    'AreaApplicationId': 'AreaApplication',
    'GroundCertificateId': 'GroundCertificate'
};

// Fields that need to be converted to string (number -> string)
export const STRING_FIELDS = [
    'CadastralRegisteredArea'
];
